import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { ENTITY_NAMES } from "../constants/entities";
import LabForm from "../components/labs/LabForm";
import SmartBackButton from "../components/ui/SmartBackButton";
import toast from "react-hot-toast";
import { InsertLab, InsertLabResource } from "../lib/supabase";

interface Series {
  id: string;
  title: string;
  code: string;
  type: string;
}

const AddLab: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [series, setSeries] = useState<Series | null>(null);

  useEffect(() => {
    const fetchSeries = async () => {
      if (!seriesId) return;

      const { data, error } = await supabase
        .from("series")
        .select("id, title, code, type")
        .eq("id", seriesId)
        .single();

      if (error) {
        toast.error("Error fetching series");
        return;
      }

      setSeries(data);
    };

    fetchSeries();
  }, [seriesId]);

  const handleCreateLab = async (data: InsertLab) => {
    if (!seriesId) return;

    setIsLoading(true);
    try {
      // First create the lab
      const { data: labData, error: labError } = await supabase
        .from("labs")
        .insert([
          {
            title: data.title,
            description: data.description,
            order_number: data.order_number,
            learning_outcomes: data.learning_outcomes,
            series_id: seriesId,
            mindfulness_topic: data.mindfulness_topic,
            coding_topic: data.coding_topic,
            objective: data.objective,
          },
        ])
        .select()
        .single();

      if (labError) throw labError;

      // Then create the resources
      if (data.resources && data.resources.length > 0) {
        const resources: InsertLabResource[] = data.resources.map(
          (resource) => ({
            ...resource,
            lab_id: labData.id,
          })
        );

        const { error: resourceError } = await supabase
          .from("lab_resources")
          .insert(resources);

        if (resourceError) throw resourceError;
      }

      toast.success("Saved.");
      navigate(`/series/${seriesId}`);
    } catch (error) {
      console.error("Error creating lab:", error);
      toast.error("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!series) return null;

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton
          fallbackPath={`/series/${seriesId}`}
          className="text-link hover:text-hover flex items-center"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1>
              Add {series.type === "camp" ? "Day" : ENTITY_NAMES.LAB} to {series.code}
            </h1>
          </div>

          <LabForm
            onSubmit={handleCreateLab}
            isLoading={isLoading}
            onCancel={() => navigate(`/series/${seriesId}`)}
          />
        </div>
      </div>
    </div>
  );
};

export default AddLab;
